import { DateSkipper, GraviButton, Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { Input, Select, Switch } from 'antd'
import type { DriverFilter } from '../types'
import { SHIFT_OPTIONS, SEARCH_BY_OPTIONS } from '../constants'
import { FilterFilled, SearchOutlined } from '@ant-design/icons'
import ButtonGroup from 'antd/es/button/button-group'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import '../styles.css'
import type { useDriverFilters } from '../hooks/useDriverFilters'

type DriverFiltersProps = {
  driverFiltersHook: ReturnType<typeof useDriverFilters>
}

export function DriverFilters({ driverFiltersHook }: DriverFiltersProps) {
  const { filterOptions, selectedFilters, handleFilterChange, handleDateChange, handleSearch } = driverFiltersHook
  return (
    <Vertical height={'auto'} flex={'none'} width={'100%'} className='driver-filters-container' alignItems='flex-end'>
      <Horizontal height={'auto'} width={'100%'} className='driver-filters-container' justifyContent='space-between'>
        <ButtonGroup>
          <GraviButton
            buttonText='Filter'
            icon={<FilterFilled />}
            appearance={selectedFilters.type === 'filter' ? 'outline' : 'outlined'}
            onClick={() => handleFilterChange({ type: 'filter' })}
            className='filter-tab-button'
          />
          <GraviButton
            buttonText='Search'
            icon={<SearchOutlined />}
            appearance={selectedFilters.type === 'search' ? 'outline' : 'outlined'}
            onClick={() => handleFilterChange({ type: 'search' })}
            className='search-tab-button'
          />
        </ButtonGroup>
        <div className='mb-2'>
          <DateSkipper
            daysToSkip={1}
            onChange={(_, dateString) => handleDateChange(dateString)}
            format={formatters.DATE}
            startDate={selectedFilters.date}
            disabled={selectedFilters.search_by === 'order_number' && selectedFilters.type === 'search'}
          />
        </div>
      </Horizontal>

      {selectedFilters.type === 'search' ? (
        <SearchTab
          driverOptions={filterOptions.driverOptions}
          filters={selectedFilters}
          handleFilterChange={handleFilterChange}
          handleSearch={handleSearch}
        />
      ) : (
        <FilterTab
          driverOptions={filterOptions.driverOptions}
          orderNumberOptions={filterOptions.orderNumberOptions}
          depotOptions={filterOptions.depotOptions}
          filters={selectedFilters}
          handleFilterChange={handleFilterChange}
        />
      )}
    </Vertical>
  )
}

type SearchTabProps = {
  driverOptions: { name: string; disabled: boolean }[]
  filters: DriverFilter
  handleFilterChange: ReturnType<typeof useDriverFilters>['handleFilterChange']
  handleSearch: (searchTerm: string | number) => void
}

function SearchTab({ driverOptions, filters, handleFilterChange, handleSearch }: SearchTabProps) {
  return (
    <Horizontal gap={4} className='search-tab-container p-3 mb-1' flex={1}>
      <Input.Group compact>
        <Select
          className='search-by-select'
          options={SEARCH_BY_OPTIONS}
          onChange={(value) => handleFilterChange({ search_by: value, search_term: null })}
          value={filters.search_by}
        />
        {filters.search_by === 'driver_name' ? (
          <Select
            placeholder='Enter driver name'
            className='driver-name-select'
            options={driverOptions.map((term) => ({ label: term.name, value: term.name, disabled: term.disabled }))}
            showSearch
            allowClear
            onClear={() => handleFilterChange({ search_term: null })}
            onSelect={(value) => handleSearch(value)}
            suffixIcon={null}
            value={filters.search_term || ''}
          />
        ) : (
          <Input
            placeholder='Enter order number'
            className='search-input'
            onPressEnter={(e) => handleSearch(filters.search_term || '')}
            onChange={(e) => handleFilterChange({ search_term: e.target.value })}
            suffix={
              <SearchOutlined
                onClick={() => handleSearch(filters.search_term || '')}
                className='search-icon-clickable'
              />
            }
            allowClear
            value={filters.search_term || ''}
          />
        )}
      </Input.Group>
    </Horizontal>
  )
}

type FilterTabProps = {
  driverOptions: { name: string; disabled: boolean }[]
  orderNumberOptions: number[]
  depotOptions: string[]
  filters: DriverFilter
  handleFilterChange: ReturnType<typeof useDriverFilters>['handleFilterChange']
}

function FilterTab({ driverOptions, orderNumberOptions, depotOptions, filters, handleFilterChange }: FilterTabProps) {
  return (
    <Vertical gap={4} className='filter-tab-container p-3 mb-1' flex={1}>
      <Select
        showSearch
        mode='multiple'
        allowClear
        placeholder={'By Driver Name'}
        onChange={(value) => handleFilterChange({ driver_names: value })}
        className='flex-1 filter-select-height'
        options={driverOptions.map((term) => ({ label: term.name, value: term.name }))}
        value={filters.driver_names}
        size='middle'
        maxTagCount='responsive'
      />
      <Select
        showSearch
        mode='multiple'
        allowClear
        placeholder={'By Order Number'}
        onChange={(value) => handleFilterChange({ order_numbers: value })}
        className='flex-1 filter-select-flex'
        options={orderNumberOptions.map((term) => ({ label: term.toString(), value: term }))}
        value={filters.order_numbers}
        size='middle'
        maxTagCount='responsive'
      />
      <Horizontal gap={4} className='filter-horizontal-container mb-1' flex={1} alignItems='center'>
        <Select
          mode='multiple'
          allowClear
          placeholder='By Depot'
          onChange={(value) => handleFilterChange({ depot_names: value })}
          options={depotOptions.map((depot) => ({ label: depot, value: depot }))}
          value={filters.depot_names}
          maxTagCount='responsive'
          className='filter-select-flex'
        />
        <ButtonGroup>
          {SHIFT_OPTIONS.map((shift) => (
            <GraviButton
              key={shift}
              buttonText={shift}
              appearance={filters.shift === shift ? 'solid' : 'outlined'}
              onClick={() => handleFilterChange({ shift })}
              className={filters.shift === shift ? 'shift-button-active' : 'shift-button-inactive'}
            />
          ))}
        </ButtonGroup>
      </Horizontal>
      <Horizontal gap={8} justifyContent='space-between' alignItems='center' className='mt-2'>
        <GraviButton
          buttonText='Clear All'
          onClick={() =>
            handleFilterChange({
              ...filters,
              shift: 'All',
              driver_names: [],
              order_numbers: [],
              depot_names: [],
              show_live: false,
            })
          }
          className='clear-all-button'
          appearance='outlined'
          size='small'
        />
        <Switch
          checked={filters.show_live}
          onChange={(checked) => handleFilterChange({ show_live: checked })}
          checkedChildren={
            <span style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '2px' }}>Live</span>
          }
          unCheckedChildren={
            <span style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '2px' }}>All</span>
          }
        />
      </Horizontal>
    </Vertical>
  )
}
